import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ArrowRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useLanguage, categories, skillMissionMap } from '../i18n';

const SkillsPage = () => {
  const navigate = useNavigate();
  const { t, getSkills } = useLanguage();
  
  const skills = getSkills();

  const handleSkillClick = (skill) => {
    // Redirect to missions page with skill filter
    navigate(`/missions?skill=${skill.id}`);
  };

  const getSkillsByCategory = (categoryId) => {
    return skills.filter(s => s.category === categoryId);
  };

  const getCategoryLabel = (categoryId) => {
    const categoryMap = {
      'Core': t('skills.categories.core'),
      'Tools': t('skills.categories.tools'),
      'Technical': t('skills.categories.technical'),
      'Business': t('skills.categories.business'),
      'Management': t('skills.categories.management')
    };
    return categoryMap[categoryId] || categoryId;
  };

  const getMissionCount = (skillId) => {
    const missionIds = skillMissionMap[skillId] || [];
    return missionIds.length;
  };

  return (
    <Layout>
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12 lg:mb-16">
            <span className="label-mono mb-4 block">{t('skills.label')}</span>
            <h1 className="section-title">{t('skills.title')}</h1>
            <p className="font-body text-lg text-slate-600 mt-4 max-w-2xl">
              {t('skills.description')}
            </p>
          </div>

          <div className="space-y-12">
            {categories.map((category) => {
              const categorySkills = getSkillsByCategory(category.id);
              if (categorySkills.length === 0) return null;

              return (
                <div key={category.id}>
                  <h2 className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-6 pb-2 border-b border-slate-200">
                    {getCategoryLabel(category.id)}
                  </h2>
                  <div className="space-y-4">
                    {categorySkills.map((skill) => (
                      <button
                        key={skill.id}
                        onClick={() => handleSkillClick(skill)}
                        className="group w-full text-left p-6 bg-white border border-slate-200 hover:border-blue-600 hover:bg-blue-50/30 transition-all duration-200"
                      >
                        <div className="flex items-center justify-between gap-6">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 mb-2">
                              <h3 className="font-display text-lg font-medium text-slate-800 group-hover:text-blue-600 transition-colors">
                                {skill.name}
                              </h3>
                              <span className="font-mono text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded mt-1 sm:mt-0 w-fit">
                                {getMissionCount(skill.id)} {t('skills.missions')}
                              </span>
                            </div>
                            <p className="font-body text-sm text-slate-600">
                              {skill.description}
                            </p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SkillsPage;
